import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/store/user';
import keycloak from '@/auth/keycloak';
import LandingPageView from '@/views/LandingPageView.vue';
import DashboardView from '@/views/DashboardView.vue';
import DataManagerView from '@/views/DataManagerView.vue';
import ProjectListView from '@/views/ProjectListView.vue';
import ProjectDetailView from '@/views/ProjectDetailView.vue';
import UploadManagerView from '@/views/UploadManagerView.vue';
import AdminOrganizationListView from '@/views/AdminOrganizationListView.vue';
import AdminUserListView from '@/views/AdminUserListView.vue';
import PersonalInformationView from '@/views/PersonalInformationView.vue';
import OrganizationView from '@/views/OrganizationView.vue';
import AdminUserDetailView from '@/views/AdminUserDetailView.vue';
import AdminStatsView from '@/views/AdminStatsView.vue';
import AdminInvitesListView from '@/views/AdminInvitesListView.vue';
import PageNotFoundView from '@/views/PageNotFoundView.vue';
import AdminComputeTasksView from '@/views/AdminComputeTasksView.vue';
import AdminCodesListView from '@/views/AdminCodesListView.vue';
import AdminLogsView from '@/views/AdminLogsView.vue';
import SystemDataManagerView from '@/views/SystemDataManagerView.vue';
import CodeDetail from '@/components/code/CodeDetail.vue';
import BucketDetail from '@/components/data/BucketDetail.vue';

const routes = [
  {
    path: '/',
    name: 'LandingPage',
    component: LandingPageView,
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'landing',
      title: 'Cloud Analytics',
    },
  },
  {
    path: '/mfa',
    name: 'MfaSelection',
    component: () => import('@/views/MfaSelection.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'landing',
      title: 'Multi-Factor Authentication Setup',
    },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'authenticated',
      title: 'Dashboard',
    },
  },
  {
    path: '/projects',
    name: 'Projects',
    component: ProjectListView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'authenticated',
      title: 'Projects',
    },
  },
  {
    path: '/projects/:id',
    name: 'ProjectDetail',
    component: ProjectDetailView,
    props: true, // Enables passing route params as props
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'authenticated',
      title: 'Project Detail',
    },
  },
  {
    path: '/data-manager',
    name: 'DataManager',
    component: DataManagerView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'authenticated',
      title: 'Data Manager',
    },
  },
  {
    path: '/data/:id',
    name: 'BucketDetail',
    component: BucketDetail,
    props: true, // Enables passing route params as props
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'authenticated',
      title: 'Data Browser',
    },
  },
  {
    path: '/upload-manager',
    name: 'UploadManager',
    component: UploadManagerView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'authenticated',
      title: 'Upload Manager',
    },
  },
  {
    path: '/codes',
    name: 'AdminCodesList',
    component: AdminCodesListView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: 'authenticated',
      title: 'Kits and Coupons',
    },
  },
  {
    path: '/codes/:code',
    name: 'CodeDetail',
    component: CodeDetail,
    props: true, // Enables passing route params as props
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: 'authenticated',
      title: 'Code Detail',
    },
  },
  {
    path: '/organizations',
    name: 'AdminOrganizationList',
    component: AdminOrganizationListView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: 'authenticated',
      title: 'Organizations',
    },
  },
  {
    path: '/users',
    name: 'AdminUserList',
    component: AdminUserListView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: 'authenticated',
      title: 'Users',
    },
  },
  {
    path: '/invites',
    name: 'AdminInvitesList',
    component: AdminInvitesListView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: 'authenticated',
      title: 'Invites',
    },
  },
  {
    path: '/system-data',
    name: 'SystemDataManager',
    component: SystemDataManagerView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: 'authenticated',
      title: 'System Data Manager',
    },
  },
  {
    path: '/stats',
    name: 'AdminStats',
    component: AdminStatsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: 'authenticated',
      title: 'Stats',
    },
  },
  {
    path: '/users/:id',
    name: 'AdminUserDetail',
    component: AdminUserDetailView,
    props: true, // Enables passing route params as props
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: 'authenticated',
      title: 'User Detail',
    },
  },
  {
    path: '/compute-tasks',
    name: 'AdminComputeTasks',
    component: AdminComputeTasksView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: 'authenticated',
      title: 'Compute Tasks',
    },
  },
  {
    path: '/logs',
    name: 'AdminLogs',
    component: AdminLogsView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: 'authenticated',
      title: 'Logs',
    },
  },
  {
    path: '/personal-information',
    name: 'PersonalInformation',
    component: PersonalInformationView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'authenticated',
      title: 'Personal Information',
    },
  },
  {
    path: '/organization-settings',
    name: 'OrganizationSettings',
    component: OrganizationView,
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'authenticated',
      title: 'Organization Settings',
    },
  },
  // BaseSpace OAuth callback route
  {
    path: '/basespace',
    name: 'BaseSpaceView',
    component: () => import('@/views/BaseSpaceView.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'authenticated',
      title: 'BaseSpace',
    },
  },
  {
    path: '/basespace-callback',
    name: 'BaseSpaceCallback',
    component: () => import('@/views/BaseSpaceCallback.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: false,
      layout: 'authenticated',
      title: 'BaseSpace Authentication',
    },
  },
  // Public document routes
  /* Disable the original terms and conditions, cloud terms and privacy policy,
   * and use the documents published on www.samplinghuman.com instead.
  {
    path: '/terms-and-conditions',
    name: 'TermsAndConditions',
    component: () =>
      import('@/components/publicDocuments/TermsAndConditions.vue'),
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'publicDocument',
      title: 'Terms and Conditions',
    },
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import('@/components/publicDocuments/PrivacyPolicy.vue'),
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'publicDocument',
      title: 'Privacy Policy',
    },
  },
  {
    path: '/cloud-terms',
    name: 'CloudTerms',
    component: () => import('@/components/publicDocuments/CloudTerms.vue'),
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'publicDocument',
      title: 'Cloud Terms of Service',
    },
  },//*/
  // Catch-all route for 404 Not Found
  {
    path: '/:pathMatch(.*)*',
    name: 'PageNotFound',
    component: PageNotFoundView,
    meta: {
      requiresAuth: false,
      requiresAdmin: false,
      layout: 'landing',
      title: 'Page Not Found',
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Navigation guard for authentication
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();
  document.title = to.meta.title as string;
  const requiresAdmin = to.meta.requiresAdmin;
  const requiresAuth = to.meta.requiresAuth;

  // Redirect to login if not authenticated
  if (requiresAuth && !userStore.authenticated) {
    keycloak.login();
  }
  // Enforce MFA setup if no MFA item contains '2fa' string
  else if (
    requiresAuth &&
    userStore.authenticated &&
    userStore.mfaNotConfigured &&
    process.env.VUE_APP_REQUIRED_MFA === 'true'
  ) {
    // Refresh MFA methods from keycloak
    console.debug('MFA check...');
    await userStore.fetchUserCredentials();

    // Prevent access to /mfa if MFA is already configured
    if (to.path === '/mfa' && !userStore.mfaNotConfigured) {
      next('/dashboard');
      return;
    } else if (!userStore.mfaNotConfigured) {
      console.debug('MFA methods found, proceeding to requested page');
      next(); // MFA requirement satisfied after refresh, proceed normally
      return;
    } else if (to.path === '/mfa') {
      console.debug(
        'MFA not configured, /mfa route requested, proceeding to MFA selection'
      );
      next(); // Already at MFA selection page, proceed
      return;
    }

    // otherwise redirect to personal information to set up MFA
    console.debug('No MFA methods found, redirecting to /mfa');
    next('/mfa');
  }
  // Prevent access to /mfa if MFA is already configured
  else if (to.path === '/mfa' && !userStore.mfaNotConfigured) {
    console.debug('MFA already configured, redirecting to /dashboard');
    next('/dashboard');
  }
  // Redirect standard users to dashboard from admin-level pages
  else if (requiresAdmin && !userStore.admin) {
    next('/dashboard');
  }
  // Redirect authenticated users away from landing page
  else if (to.path === '/' && userStore.authenticated) {
    next('/dashboard');
  }
  // Proceed as normal
  else {
    // All checks passed, proceed to route
    next();
  }
});

export default router;
